import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import StatusBadgeCst, { StatusBadgeVariant } from "@components/StatusBadgeCst";

export interface UserRowBadge {
  label: string;
  variant: StatusBadgeVariant;
}

interface UserRowProps {
  id: string;
  name: string;
  email?: string;
  extId?: string;
  status?: string;
  badges?: UserRowBadge[];
}

const UserRow: React.FC<UserRowProps> = ({
  id,
  name,
  email,
  extId,
  status,
  badges,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const normalizedStatus = (status || "").toLowerCase();
  const isActive = normalizedStatus === "active";
  const isSuspended = normalizedStatus === "suspended";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${id}${extId ? ` | Ext. ID: ${extId}` : ""}${email ? ` | ${email}` : ""}`}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isActive && (
          <StatusBadgeCst
            label={String(t("row.active", { ns: "users-and-groups" }))}
            variant="active"
          />
        )}
        {isSuspended && (
          <StatusBadgeCst
            label={String(t("row.suspended", { ns: "users-and-groups" }))}
            variant="suspended"
          />
        )}
        {badges?.map((badge) => (
          <StatusBadgeCst
            key={badge.label}
            label={badge.label}
            variant={badge.variant}
          />
        ))}
      </Box>
    </Box>
  );
};

export default UserRow;
